/**
 * editorLayout — pure rules for how an editor surface composes with its
 * companion "view" (the live board, a process diagram, …), kept out of the
 * React hook/component so they can be unit-tested and reused across the
 * composable IDE framework.
 *
 * A tab's *view mode* is one `EditorLayout`. Which modes a tab type offers, and
 * which it opens at, is declared ONCE per tab kind in `VIEW_MODE_BY_KIND` — the
 * single place that encodes "code splits beside its board", "a doc opens solo",
 * "a process opens full". Every surface that shows the layout picker reads this,
 * so per-type behaviour stays consistent everywhere.
 *
 *   editor-only   editor fills; the view is hidden (kept reachable via the menu)
 *   view-only     the view fills; the editor is hidden
 *   split-right   editor left  | view right
 *   split-bottom  editor top   / view bottom
 *   split-top     view top     / editor bottom
 *
 * Board visibility: the board is on screen for a board tab, and for an editor
 * tab unless it's collapsed to editor-only. The right details panel + its toggle
 * belong to the board, so they follow this.
 */
import { type EditorLayout } from "./primitives/EditorLayoutMenu";

/** Every editor/view layout value (for storage validation + menu order). */
export const EDITOR_LAYOUTS = [
  "editor-only",
  "view-only",
  "split-right",
  "split-bottom",
  "split-top",
] as const;

/** Narrow an unknown (e.g. a persisted string) to a valid `EditorLayout`. */
export function isEditorLayout(x: unknown): x is EditorLayout {
  return typeof x === "string" && (EDITOR_LAYOUTS as readonly string[]).includes(x);
}

/** Tab kinds that pair an editor surface with a companion view, and so carry a
 *  view-mode picker. (`board` / `types` tabs have no editor → no picker.) */
export type EditorTabKind = "file" | "doc" | "process";

export interface ViewModeConfig {
  /** Modes this kind offers, in menu order (a subset of `EDITOR_LAYOUTS`). */
  readonly options: readonly EditorLayout[];
  /** Mode a fresh tab of this kind opens at. */
  readonly default: EditorLayout;
  /** When true the default is "sticky": the user's last pick for this kind
   *  becomes the default for future tabs of the kind (so choosing a split on
   *  one code tab makes new code tabs open the same way). When false every new
   *  tab of the kind opens at `default`. */
  readonly stickyDefault?: boolean;
}

/**
 * Per-kind view-mode behaviour — the heart of the unified open behaviour.
 *
 *  - file:    code beside its board; remembers the last split you chose.
 *  - doc:     prose opens solo; the board is opt-in.
 *  - process: opens FULL (the diagram is the point); code is opt-in and, since
 *             it's not sticky, every process still opens full by default.
 *             `editor-only` is omitted — code with no diagram defeats the tab.
 *             NOTE: `view-only` here means the editor PANE is hidden, NOT that
 *             the process is read-only — the diagram surfaces (tree/flow/
 *             sequence/BPMN) carry their own WYSIWYG step-edit affordances,
 *             gated on `canEdit` (a mutation service present), exactly like the
 *             board. Editing a process visually never requires opening the code.
 */
export const VIEW_MODE_BY_KIND: Record<EditorTabKind, ViewModeConfig> = {
  file: {
    options: ["editor-only", "view-only", "split-right", "split-bottom", "split-top"],
    default: "split-right",
    stickyDefault: true,
  },
  doc: {
    options: ["editor-only", "view-only", "split-right", "split-bottom", "split-top"],
    default: "editor-only",
  },
  process: {
    options: ["view-only", "split-right", "split-bottom", "split-top"],
    default: "view-only",
  },
};

/** The mode a fresh tab of `kind` opens at — the sticky last choice for kinds
 *  that remember it, else the kind's fixed default. */
export function defaultLayoutFor(kind: EditorTabKind, stickyLast: EditorLayout): EditorLayout {
  const cfg = VIEW_MODE_BY_KIND[kind];
  return cfg.stickyDefault ? stickyLast : cfg.default;
}

/** The view modes `kind` offers in its picker. */
export function layoutOptionsFor(kind: EditorTabKind): readonly EditorLayout[] {
  return VIEW_MODE_BY_KIND[kind].options;
}

/** Whether the board is on screen — drives the details panel + its toggle. The
 *  board is hidden only when the editor is solo (editor-only); view-only and the
 *  splits all keep it on screen. */
export function isBoardVisible(args: {
  readonly isBoardView: boolean;
  readonly showEditor: boolean;
  readonly editorLayout: EditorLayout;
}): boolean {
  return (
    args.isBoardView && !(args.showEditor && args.editorLayout === "editor-only")
  );
}
